<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { IonContent, IonIcon, IonSpinner } from '@ionic/vue';
import { useRoute } from 'vue-router';
import { timeOutline } from 'ionicons/icons';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';

import FavoriteButton from '@/shared/components/FavoriteButton.vue';
import BackButton from '@/shared/components/BackButton.vue';
import FullScreenImage from '@/shared/components/ui/FullScreenImage.vue';
import RelatedContent from '@/shared/components/ui/RelatedContent.vue';
import ShareButton from '@/shared/components/ShareButton.vue';
import TopBar from '@/shared/components/TopBar.vue';
import UnsafeHtmlRenderer from '@/shared/components/UnsafeHtmlRenderer.vue';
import usePosts from '@/modules/news/composables/usePosts';
import useUtils from '@/shared/composables/useUtils';
import useTenant from '@/shared/composables/useTenant';
import { useCacheImage } from '@/shared/composables/useCacheImage';
import { useAuthStore } from '@/stores/auth/authStore';

// Init
const { t } = useI18n();
const route = useRoute();
// Reactively compute postId from route params and validate it's a valid number
const postId = computed(() => {
	const id = route.params.id;
	if (!id || typeof id !== 'string') {
		return NaN;
	}
	const numId = +id;
	return isNaN(numId) ? NaN : numId;
});
const { getPost, markPostAsViewed } = usePosts();
const authStore = useAuthStore();
// Only enable the query when postId is a valid number
const isValidPostId = computed(() => !isNaN(postId.value));
const { isFetchedPost, post } = getPost(postId, isValidPostId);
const { transformOembedToIframe } = useUtils();
const { getTenantConfigAndStyles } = useTenant();
const { showLoggedData } = getTenantConfigAndStyles();
const { loadImage } = useCacheImage();
const postImages = ref<string[]>([]);
const postMainImage = ref<string>('');
// Normalize returnTo to ensure it's a valid absolute path
const returnTo = computed(() => {
	const returnToValue = route.query.returnTo as string;
	if (!returnToValue) {
		return '/dashboard';
	}
	// If returnTo is 'feed', map it to '/dashboard' since there's no '/feed' route
	if (returnToValue === 'feed') {
		return '/dashboard';
	}
	// Ensure returnTo starts with '/' to make it an absolute path
	return returnToValue.startsWith('/') ? returnToValue : `/${returnToValue}`;
});

watch(
	post,
	async (post) => {
		if (post && !isNaN(postId.value) && authStore.isAuthenticated()) {
			markPostAsViewed(postId.value);
		}
		if (post?.main_image_url && !isNaN(postId.value)) {
			let postMainImg = post.main_image_url.split('/').pop();
			postMainImg = postMainImg ?? 'post';
			postMainImage.value = await loadImage(`post-${postId.value}-main-image`, postMainImg, post.main_image_url);
		}
		if (post?.images && !isNaN(postId.value)) {
			post.images.forEach(async (image: any, index: number) => {
				let postImg = image.image_url.split('/').pop();
				postImg = postImg ?? 'post';
				const imageUrl = await loadImage(`post-${postId.value}-image-${index}`, postImg, image.image_url);
				postImages.value.push(imageUrl);
			});
		}
	},
	{ immediate: true },
);

// Image handling
const fullScreenImage = ref('');
const openInFullScreen = ref(false);
const viewFullScreen = (image: string) => {
	fullScreenImage.value = image;
	openInFullScreen.value = true;
};

const onCloseImage = () => {
	openInFullScreen.value = false;
};
</script>

<template>
	<ion-content>
		<FullScreenImage v-if="openInFullScreen" :fullScreenImage="fullScreenImage" @onCloseImage="onCloseImage" />
		<TopBar class="px-[34px]">
			<template #left>
				<BackButton :to="returnTo" />
			</template>

			<template #center>
				<div
					v-if="post"
					class="flex justify-center w-full font-sourcesanspro-semibold text-l2 truncate"
					style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
					:title="post.title"
				>
					<span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; max-width: 100%;">
						{{ post.title.length > 50 ? post.title.slice(0, 50) + '…' : post.title }}
					</span>
				</div>
			</template>

			<template v-if="post" #right>
				<FavoriteButton v-if="showLoggedData()" :id="post.id" :favorite="post.is_favorite" :type="post.type"></FavoriteButton>
				<ShareButton :id="post.id" :title="post.title" type="news"></ShareButton>
			</template>
		</TopBar>

		<div class="flex items-center justify-center" v-if="!isFetchedPost && isValidPostId">
			<ion-spinner name="dots"></ion-spinner>
		</div>

		<div v-if="post && isFetchedPost">
			<div v-if="postMainImage || postImages.length > 0" class="flex overflow-scroll w-[100%] h-[182px] mb-4 invisible-scroller">
				<div v-if="postMainImage" class="flex items-center min-w-[326px] mr-4 rounded-xl overflow-hidden ml-[34px]">
					<div class="w-full h-full bg-black flex items-center justify-center">
						<img :src="postMainImage" @click="viewFullScreen(postMainImage)" class="object-contain w-full h-full"
							style="background-color: black;"
						/>
					</div>
				</div>
				<div v-for="(image, idx) in postImages" :key="idx" class="flex items-center min-w-[326px] mr-4 rounded-xl overflow-hidden"
					:class="[{ 'ml-[34px]': idx === 0 }]">
					<div class="w-full h-full bg-black flex items-center justify-center">
						<img
							:src="image"
							@click="viewFullScreen(image)"
							class="object-contain w-full h-full"
							style="background-color: black;"
						/>
					</div>
				</div>
			</div>

			<div class="px-[34px] mb-2">
				<div v-if="post.show_metadata !== false" class="flex font-sourcesanspro-regular text-xs2 font-color-text-50 mb-4">
					<div class="mr-2">
						{{ DateTime.fromISO(post?.created_at).toFormat('dd MMMM yyyy') }}
					</div>
					<div class="flex items-center">
						<ion-icon class="mr-1 text-m2 font-color-text-50" :icon="timeOutline"></ion-icon>
						{{ DateTime.fromISO(post?.created_at).toFormat('HH:mm') }}
					</div>
				</div>
				<UnsafeHtmlRenderer
					:html="transformOembedToIframe(post.content)"
					:unsafe="Boolean(post.full_html)"
					class="font-sourcesanspro-regular text-base2 mb-4 font-white" />
			</div>

			<div v-if="post.events.length" class="mb-2 font-sourcesanspro-semibold">
				<RelatedContent
					:title="t('components.related_content.title_events')"
					:contents="
						post.events.map((event) => {
							return {
								'link': `/events/${event.id}`,
								'src': event.main_image_url,
								'title': event.name,
								'subtitle': '',
							};
						})
					" />
			</div>

			<div class="h-[100px] mb-4">&nbsp;</div>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped></style>
